import { add, addDays, isAfter, isBefore, isSameDay } from "date-fns";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";

const isDateDisabled = (
  disabledDays: Date[],
  currentDate: Date,
  range: number
) => {
  if (isEmpty(disabledDays)) return false;

  // Define the start and end dates of the purchase period
  const purchaseStart = currentDate;
  const purchaseEnd = add(currentDate, { days: range });

  // Check if any disabled day falls within the purchase period
  return disabledDays.some(
    (disabledDay) =>
      isSameDay(disabledDay, purchaseStart) ||
      isSameDay(disabledDay, purchaseEnd) ||
      (isAfter(disabledDay, purchaseStart) &&
        isBefore(disabledDay, purchaseEnd))
  );
};

/**
 * useFirstAvailableDate is used on AddToCartSection where it calculates the booking date of an item for checkout
 */
export const useFirstAvailableDate = ({
  disabledDays,
  preparationDays,
  maximumShippingDays,
  minimumShippingDays,
  validRange,
}: {
  disabledDays: Date[];
  preparationDays: number;
  maximumShippingDays: number;
  minimumShippingDays: number;
  validRange: number;
}) => {
  const [firstAvailableDate, setFirstAvailableDate] = useState(new Date());
  const [firstAvailablePurchaseDateEnd, setFirstAvailablePurchaseDateEnd] =
    useState(new Date());
  const [isDisabledPurchase, setIsDisabledPurchase] = useState(false);

  useEffect(() => {
    const maxIterationDateCount = 365;

    let iterationDateCount = 0;
    let currentDate = addDays(new Date(), preparationDays);
    currentDate.setHours(12, 0, 0, 0);

    while (
      isDateDisabled(disabledDays, currentDate, validRange) &&
      iterationDateCount < maxIterationDateCount
    ) {
      currentDate = addDays(currentDate, 1);

      ++iterationDateCount;
    }

    const availableDate = addDays(
      currentDate,
      maximumShippingDays + minimumShippingDays
    );
    const availablePurchaseEndDate = addDays(availableDate, validRange);

    setFirstAvailableDate(availableDate);
    setFirstAvailablePurchaseDateEnd(availablePurchaseEndDate);

    const isPurchaseDisabled = disabledDays.some(
      (d) =>
        isSameDay(d, availablePurchaseEndDate) ||
        isSameDay(d, availableDate) ||
        (isAfter(d, availableDate) && isBefore(d, availablePurchaseEndDate))
    );

    setIsDisabledPurchase(isPurchaseDisabled);
  }, [
    disabledDays,
    preparationDays,
    maximumShippingDays,
    minimumShippingDays,
    validRange,
  ]);

  const resetDate = () => {
    setFirstAvailableDate(new Date());
    setFirstAvailablePurchaseDateEnd(new Date());
    setIsDisabledPurchase(false);
  };

  return {
    firstAvailableDate,
    firstAvailablePurchaseDateEnd,
    isDisabledPurchase,
    resetDate,
  };
};
